import { useMsal } from '@azure/msal-react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import React, { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';

interface FormDataType {
  name: string;
  fileName?: string;
  documentid?: number;
  description?: string;
}
interface newFile {
  fileName: string;
  description?: string;
  file: {};
}

interface EditDeleteModal {
  isOpen: boolean;
  onClose: () => void;
  id: number;
  count: number;
  setCount: any;
}

const getColor = (props: {
  isDragAccept: any;
  isDragReject: any;
  isFocused: any;
}) => {
  if (props.isDragAccept) {
    return '#00e676';
  }
  if (props.isDragReject) {
    return '#ff1744';
  }
  if (props.isFocused) {
    return '#2196f3';
  }
  return '#eeeeee';
};

function EditDocumentModal({
  isOpen,
  onClose,
  id,
  count,
  setCount,
}: EditDeleteModal) {
  let toast = useToast();
  const [documentData, setDocumentData] = React.useState<
    FormDataType | undefined
  >({
    documentid: 0,
    name: '',
    description: '',
  });
  const [file, setFile] = React.useState<newFile | undefined>({
    fileName: '',
    description: '',
    file: {},
  });

  const { accounts } = useMsal();
  const userdetails = JSON.parse(localStorage.getItem('userdetails') || '');
  const userid =
    userdetails[0].portal_Id != ''
      ? userdetails[0].portal_Id
      : accounts[0].username;

  useEffect(() => {
    if (id !== 0) {
      const baseurl = process.env.REACT_APP_PAYER_FORM_BASE_URL as string;
      const tokenUrl = process.env.REACT_APP_TOKEN_URL_PAYER_FORM as string;

      // Function to fetch the forms
      const fetchForms = async (id: number) => {
        try {
          // Fetch the token
          const tokenResponse = await axios.get(tokenUrl);
          if (tokenResponse.data.success) {
            const response = await axios.get(
              baseurl + '/App/api/get_form' + '?documentid=' + id,
              {
                headers: {
                  Authorization: `Bearer ${tokenResponse.data.token}`,
                },
              }
            );
            setDocumentData(response.data.form);
          } else {
            throw new Error('Failed to retrieve auth token');
          }
        } catch (error) {
          console.error('Error downloading file:', error);
        }
      };
      fetchForms(id);
    }
  }, [id]);

  useEffect(() => {}, [file, documentData]);

  const saveDocument = async (data: FormDataType, file: newFile) => {
    const tokenUrl = process.env.REACT_APP_TOKEN_URL_PAYER_FORM as string;
    const baseurl = process.env.REACT_APP_PAYER_FORM_BASE_URL as string;

    try {
      // Fetch token from tokenUrl
      const tokenResponse = await axios.get(tokenUrl);
      const tokenData = tokenResponse.data;
      const token = tokenData.token;

      // Post data to saveFormUrl using Axios
      const response = await axios.post(
        baseurl + '/App/api/save_form?username=' + userid,
        {
          name: documentData?.name,
          documentid: documentData?.documentid,
          description: documentData?.description,
        },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setCount(count + 1);
        toast({
          title: 'Success',
          position: 'top',
          description: 'Document Successfully Edited!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Handle error if data couldn't be posted
        console.error('Error adding data');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = new FileReader();

      file.readAsDataURL(acceptedFiles[0]);

      setFile(prevFile => ({
        ...prevFile,
        fileName: acceptedFiles[0].name,
        file: acceptedFiles[0],
      }));

      setDocumentData(prevFile => ({
        ...prevFile,
        name: acceptedFiles[0].name,
      }));
    },
    [setFile]
  );

  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
    },
    maxFiles: 1,
  });

  const borderColor = getColor({ isDragAccept, isDragReject, isFocused });

  const fileRejectionItems = fileRejections.map(({ file, errors }, index) => (
    <Text key={index} color="red.500">
      {file.name} -{' '}
      {errors
        .map(error => {
          if (error.code === 'file-invalid-type') {
            return `File type must be .pdf`;
          }
          return error.message;
        })
        .join(', ')}
    </Text>
  ));

  const handleSubmit = (event: any) => {
    event.preventDefault(); // Prevent the default form submission behavior
    if (documentData && file) saveDocument(documentData, file);
    setCount(count + 1);
    onClose();
  };

  return (
    <>
      <Modal isCentered isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader textAlign="center">Edit Payer Form</ModalHeader>
            <ModalCloseButton color="white" bg=" #002677" />
            <ModalBody>
              <FormControl isRequired>
                <FormLabel>Name:</FormLabel>
                <Input
                  placeholder="Name"
                  value={documentData ? documentData.name : ''}
                  onChange={event => {
                    const newFormName = event.target.value;
                    setDocumentData(prevDocumentData => ({
                      ...prevDocumentData,
                      name: newFormName,
                    }));
                  }}
                />
              </FormControl>
              <FormControl mt={4} isRequired>
                <FormLabel>Description:</FormLabel>
                <Input
                  placeholder="Description"
                  value={documentData ? documentData.description : ''}
                  onChange={event => {
                    const newFormDesc = event.target.value;
                    setDocumentData(prevDocumentData => ({
                      ...prevDocumentData,
                      description: newFormDesc,
                    }));
                  }}
                />
              </FormControl>
              <FormControl mt={4} isRequired>
                <FormLabel>PDF File:</FormLabel>
                <Button
                  onClick={open}
                  fontSize="12px"
                  w="fit-content"
                  h="25px"
                  bg="#002677"
                  textColor="white"
                  mb="5px"
                >
                  Upload File
                </Button>
                <Flex
                  {...getRootProps()}
                  flex="1"
                  flexDirection="column"
                  alignItems="center"
                  p="20px"
                  borderWidth="2px"
                  borderRadius="2px"
                  borderColor={borderColor}
                  borderStyle="dashed"
                  backgroundColor="#fafafa"
                  color="#bdbdbd"
                  outline="none"
                  transition="border .24s ease-in-out"
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <Text>Drop the files here ...</Text>
                  ) : (
                    <Flex display="flex" flexDir="column">
                      <Text textAlign="center">Drag and drop here ...</Text>
                      <Text textAlign="center">
                        Only *.pdf files will be accepted!
                      </Text>
                    </Flex>
                  )}
                </Flex>
                <Flex>
                  {fileRejectionItems.length !== 0
                    ? file?.fileName
                    : documentData?.name}
                </Flex>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button
                bg="#002677"
                textColor="white"
                mr={3}
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Spacer />
              <Button
                variant="ghost"
                bg="#002677"
                textColor="white"
                type="submit"
              >
                Save Form
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
}

export default EditDocumentModal;
